import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import DevicesOutlined from "@mui/icons-material/DevicesOutlined";
import DeleteOutlined from "@mui/icons-material/DeleteOutlined";
import LockResetOutlined from "@mui/icons-material/LockResetOutlined";
import KeyOutlined from "@mui/icons-material/KeyOutlined";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import { PHARMACY_HALO_COLORS } from "@/config/colors";

function fmtWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("en-AU", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" });
}

// Trusted-device rescue panel for the ph-web login. Head office can see a
// chemist's trusted browsers, forget one/all, and (when a mailbox is down)
// mint a manual code read out over the phone.
export default function ChemistTrustedDevices({ chemistUUID }) {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [confirmReset, setConfirmReset] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  const [manualCode, setManualCode] = useState(null);

  const load = useCallback(async () => {
    if (!chemistUUID) return;
    try {
      setLoading(true);
      setError(null);
      const res = await phappsRequest(
        API_PATHS.ADMIN_CHEMIST_TRUSTED_DEVICES,
        "GET",
        null,
        { ChemistUUID: chemistUUID }
      );
      setDevices(res?.Devices ?? []);
    } catch {
      setError("Failed to load trusted devices.");
    } finally {
      setLoading(false);
    }
  }, [chemistUUID]);

  useEffect(() => { load(); }, [load]);

  async function handleForget(id) {
    try {
      setBusy(true);
      await phappsRequest(API_PATHS.ADMIN_FORGET_TRUSTED_DEVICE, "POST", { ChemistUUID: chemistUUID, DeviceId: id });
      setDevices((prev) => prev.filter((d) => d.Id !== id));
    } catch {
      setNotice({ severity: "warning", text: "Failed to forget device." });
    } finally {
      setBusy(false);
    }
  }

  async function handleResetAll() {
    try {
      setBusy(true);
      const res = await phappsRequest(API_PATHS.ADMIN_RESET_CHEMIST_TRUST, "POST", { ChemistUUID: chemistUUID });
      setDevices([]);
      setConfirmReset(false);
      setNotice({ severity: "success", text: `Cleared ${res?.DevicesRevoked ?? 0} device(s) and ended ${res?.SessionsRevoked ?? 0} session(s). They'll re-verify by email next login.` });
    } catch {
      setNotice({ severity: "warning", text: "Failed to reset trust." });
    } finally {
      setBusy(false);
    }
  }

  async function handleManualCode() {
    try {
      setBusy(true);
      setManualCode(null);
      const res = await phappsRequest(API_PATHS.ADMIN_GENERATE_MANUAL_DEVICE_CODE, "POST", { ChemistUUID: chemistUUID });
      setManualCode({ code: res.Code, mins: res.ExpiresInMinutes });
    } catch (e) {
      const msg = e?.response?.data?.message
        || e?.data?.message
        || "Couldn't generate a code. Ask the user to enter their email + password first, then try again.";
      setNotice({ severity: "info", text: msg });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box sx={{ mt: 0.5 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
        <DevicesOutlined sx={{ fontSize: 16, color: PHARMACY_HALO_COLORS.BLUE }} />
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4, fontSize: "0.65rem" }}
        >
          Trusted Devices (Web Login)
        </Typography>
      </Box>

      {loading && <CircularProgress size={14} sx={{ color: PHARMACY_HALO_COLORS.BLUE, ml: 0.5 }} />}
      {error && <Typography variant="caption" color="error">{error}</Typography>}

      {!loading && devices.length === 0 && !error && (
        <Typography variant="caption" sx={{ color: "text.disabled", fontStyle: "italic" }}>
          No trusted devices
        </Typography>
      )}

      {/* Device list */}
      <Box sx={{ maxWidth: 480, maxHeight: 160, overflowY: "auto", pr: devices.length > 3 ? 0.5 : 0 }}>
        {devices.map((row) => (
          <Box
            key={row.Id}
            sx={{
              display: "flex", alignItems: "center", gap: 0.75, mb: 0.5, py: 0.4, px: 0.75,
              borderRadius: 1, backgroundColor: "#f8fafc", border: "1px solid #e5e7eb",
              "&:hover .row-actions": { opacity: 1 },
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                {row.Label || "Unknown device"}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary", display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {row.IP || "—"} · last used {fmtWhen(row.LastUsedAt)}
              </Typography>
            </Box>
            <Box className="row-actions" sx={{ display: "flex", gap: 0.25, opacity: 0, transition: "opacity 0.15s", flexShrink: 0, ml: "auto" }}>
              <Tooltip title="Forget this device">
                <span>
                  <IconButton size="small" disabled={busy} onClick={() => handleForget(row.Id)} sx={{ p: 0.3, color: "#dc2626" }}>
                    <DeleteOutlined sx={{ fontSize: 14 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Actions */}
      <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
        <Button
          size="small"
          variant="outlined"
          color="error"
          startIcon={<LockResetOutlined sx={{ fontSize: 16 }} />}
          disabled={busy}
          onClick={() => setConfirmReset(true)}
          sx={{ textTransform: "none" }}
        >
          Reset all trust
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<KeyOutlined sx={{ fontSize: 16 }} />}
          disabled={busy}
          onClick={handleManualCode}
          sx={{ textTransform: "none", borderColor: PHARMACY_HALO_COLORS.BLUE, color: PHARMACY_HALO_COLORS.BLUE }}
        >
          Generate manual code
        </Button>
      </Box>

      {manualCode && (
        <Alert severity="success" sx={{ mt: 1 }} onClose={() => setManualCode(null)}>
          Read this code to the user (expires in {manualCode.mins} min):{" "}
          <strong style={{ fontSize: "1.15rem", letterSpacing: "0.15em" }}>{manualCode.code}</strong>
        </Alert>
      )}

      {notice && (
        <Alert severity={notice.severity} sx={{ mt: 1 }} onClose={() => setNotice(null)}>
          {notice.text}
        </Alert>
      )}

      {/* Reset-all confirmation */}
      <Dialog open={confirmReset} onClose={() => setConfirmReset(false)} maxWidth="xs" PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Reset all device trust?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This forgets every trusted browser and ends all active web sessions for this chemist.
            They'll have to verify by email on their next login. Use this for a lost or stolen device.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmReset(false)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button variant="contained" color="error" disabled={busy} onClick={handleResetAll} sx={{ textTransform: "none", fontWeight: 600 }}>
            {busy ? "Resetting…" : "Reset trust"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
